import {
  LayoutDashboard,
  LibraryBig,
  SquareKanban,
  Workflow,
} from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router";
import { featureFlags } from "~/config/featureFlags";
import { PlatformRole } from "~/config/types";
import { useUserContext } from "~/hooks/useUserContext";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "~/atoms/navigation-menu";

export const MainNav = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const location = useLocation();
  const canManagePlatform =
    user.role === PlatformRole.coordinator ||
    user.role === PlatformRole.superAdmin;
  const isWorkflowTemplatesEnabled = useFeatureFlagEnabled(
    featureFlags.workflowTemplates,
  );

  return (
    <NavigationMenu className="[&>div.absolute]:-right-15 [&>div.absolute]:left-auto">
      <NavigationMenuList>
        <NavigationMenuItem>
          <Link to="/">
            <LayoutDashboard className="mr-1 h-5 w-5" />
            {t("dashboard.header")}
          </Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/documents">
            <LibraryBig className="mr-1 h-5 w-5" />
            {t("library.header")}
          </Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/chart">
            <Workflow className="mr-1 h-5 w-5" />
            {t("chart.header")}
          </Link>
        </NavigationMenuItem>
        {(user.role === PlatformRole.contributor ||
          (!isWorkflowTemplatesEnabled &&
            user.role !== PlatformRole.reader)) && (
          <NavigationMenuItem>
            <Link to="/workflows">
              <SquareKanban className="mr-1 h-5 w-5" />
              {t("commons.workflows.workflow", {
                count: 2,
              })}
            </Link>
          </NavigationMenuItem>
        )}
        {isWorkflowTemplatesEnabled && canManagePlatform && (
          <NavigationMenuItem>
            <NavigationMenuTrigger
              data-active={
                location.pathname.startsWith("/workflows") ||
                location.pathname.startsWith("/workflow-templates")
                  ? ""
                  : undefined
              }
              triggerMode="click"
            >
              <SquareKanban className="mr-1 h-5 w-5" />
              {t("commons.workflows.workflow", {
                count: 2,
              })}
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="grid w-56 gap-1 p-2">
                <li>
                  <NavigationMenuLink asChild>
                    <NavLink
                      to="/workflows"
                      className="hover:bg-accent hover:text-accent-foreground flex rounded-md py-2 text-sm font-medium"
                    >
                      {t("commons.workflows.ongoingAndUpcoming")}
                    </NavLink>
                  </NavigationMenuLink>
                </li>
                <li>
                  <NavigationMenuLink asChild>
                    <NavLink
                      to="/workflow-templates"
                      className="hover:bg-accent hover:text-accent-foreground flex items-center rounded-md py-2 text-sm font-medium"
                    >
                      {t("workflowTemplates.navLabel")}
                    </NavLink>
                  </NavigationMenuLink>
                </li>
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

const Link = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const location = useLocation();
  const isActive =
    to === "/" ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <NavigationMenuLink
      className={navigationMenuTriggerStyle()}
      active={isActive}
      asChild
    >
      <NavLink to={to}>{children}</NavLink>
    </NavigationMenuLink>
  );
};
